import classNames from 'classnames'
import { useEffect, useRef, useState } from 'react'

import Icon from '@/components/Icon/Icon'

import styles from './SideScroller.module.scss'

type Props = {
  children: React.ReactNode
  disableSideWheel?: boolean
  narrow?: boolean
}

const SideScroller = ({ children, disableSideWheel, narrow }: Props) => {
  const containerRef = useRef<HTMLDivElement>(null)
  const outerContainerRef = useRef<HTMLDivElement>(null)
  const [scrollStatus, setScrollStatus] = useState({
    left: 0,
    right: 0,
  })

  const velocity = useRef(0)
  const animationFrame = useRef<number>()

  useEffect(() => {
    const container = containerRef.current
    const outerContainer = outerContainerRef.current
    if (!container || !outerContainer) return

    const onWheel = (e: WheelEvent) => {
      const isHovering = container.matches(':hover')
      if (!isHovering) return
      if (!container.contains(e.target as Node)) return
      // If there is no horizontal scroll in the container, return
      if (container.scrollWidth <= container.clientWidth) return

      e.preventDefault()
      velocity.current += (10 * e.deltaY) / Math.abs(e.deltaY)
      startAnimation()
    }

    const startAnimation = () => {
      if (animationFrame.current) return

      const step = () => {
        const current = container
        if (!current) return

        current.scrollLeft += velocity.current
        velocity.current *= 0.9 // damping

        if (Math.abs(velocity.current) > 0.5) {
          animationFrame.current = requestAnimationFrame(step)
        } else {
          velocity.current = 0
          animationFrame.current = undefined
        }
      }

      animationFrame.current = requestAnimationFrame(step)
    }

    const getScrollStatus = () => {
      // Check the number of children elements that are out of frame to the left and right
      const children = [...container.children]
      const left = children.filter((child) => {
        const rect = (child as HTMLElement).getBoundingClientRect()
        return (
          rect.left < container.getBoundingClientRect().left || rect.left < outerContainer.getBoundingClientRect().left
        )
        // return rect.left < container.getBoundingClientRect().left || rect.left < 0
      }).length
      const right = children.filter((child) => {
        const rect = (child as HTMLElement).getBoundingClientRect()
        return (
          rect.right > container.getBoundingClientRect().right + 1 ||
          rect.right > outerContainer.getBoundingClientRect().right + 1
        )
        // return rect.right > container.getBoundingClientRect().right || rect.right > window.innerWidth
      }).length

      setScrollStatus({ left, right })
    }

    getScrollStatus()

    const resizeObserver = new ResizeObserver(getScrollStatus)

    if (!disableSideWheel) document.addEventListener('wheel', onWheel, { passive: false })
    container.addEventListener('scroll', getScrollStatus, { passive: true })
    resizeObserver.observe(container)

    return () => {
      document.removeEventListener('wheel', onWheel)
      container.removeEventListener('scroll', getScrollStatus)
      resizeObserver.unobserve(container)
    }
  }, [])

  const handleScrollButtonClick = (direction: 'left' | 'right') => {
    const container = containerRef.current
    if (!container) return

    const scrollAmount = 205
    const newScrollLeft =
      direction === 'left' ? container.scrollLeft - scrollAmount : container.scrollLeft + scrollAmount

    container.scrollTo({
      behavior: 'smooth',
      left: newScrollLeft,
    })
  }

  return (
    <div>
      <div className={styles.outerContainer} ref={outerContainerRef}>
        <button
          className={classNames(
            styles.scrollButton,
            !!scrollStatus.left && styles.activeScrollButton,
            narrow && styles.narrowLeft,
          )}
          onClick={() => handleScrollButtonClick('left')}
        >
          <div className='position-relative'>
            <Icon icon='arrowLeft' />
            {scrollStatus.left - 1 > 0 && <div className={styles.buttonLabel}>+{scrollStatus.left - 1}</div>}
          </div>
        </button>
        <div className={classNames(styles.middleContainer, narrow && styles.narrowMiddle)}>
          <div className={styles.innerContainer} ref={containerRef}>
            {children}
          </div>
        </div>
        <button
          className={classNames(
            styles.scrollButton,
            styles.rightScrollButton,
            !!scrollStatus.right && styles.activeScrollButton,
            narrow && styles.narrowRight,
          )}
          onClick={() => handleScrollButtonClick('right')}
        >
          <div className='position-relative'>
            <Icon icon='arrowRight' />
            {scrollStatus.right - 1 > 0 && <div className={styles.buttonLabel}>+{scrollStatus.right - 1}</div>}
          </div>
        </button>
      </div>
    </div>
  )
}

export default SideScroller
